import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot, MessageCircle, Info, History, Star, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import ConversationHistory from './ConversationHistory';
import ConversationViewer from './ConversationViewer';

interface EnhancedAIAssistantProps {
  onOpenFloatingChat?: () => void;
  onContinueConversation?: (conversationId: string) => void;
}

const EnhancedAIAssistant: React.FC<EnhancedAIAssistantProps> = ({
  onOpenFloatingChat,
  onContinueConversation
}) => {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const { hasAIAccess, isAdmin } = useAuth();

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setActiveTab('viewer');
  };

  const handleBackToHistory = () => {
    setSelectedConversationId(null);
    setActiveTab('history');
  };

  const handleContinueConversation = (conversationId: string) => {
    console.log('🔄 EnhancedAIAssistant - Continue conversation:', conversationId);
    if (onContinueConversation) {
      onContinueConversation(conversationId);
    } else {
      console.warn('⚠️ onContinueConversation handler not provided');
    }
  };

  const renderAIInfo = () =>
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6" data-id="88vmlcn12" data-path="src/components/ai/EnhancedAIAssistant.tsx">

      {/* Introduction */}
      <Card className="border-none bg-gradient-to-br from-blue-50 to-purple-50" data-id="rlgj4mujj" data-path="src/components/ai/EnhancedAIAssistant.tsx">
        <CardContent className="p-6" data-id="4oiub645t" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <div className="flex items-start gap-4" data-id="x6hv8nr5r" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            <div className="p-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full" data-id="1pxzwfmav" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <Bot className="h-6 w-6 text-white" data-id="yhs3k66e4" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
            </div>
            <div className="flex-1" data-id="wnpbrth16" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="xj9xwe1u7" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                AI Teaching Assistant
              </h2>
              <p className="text-gray-600 mb-4" data-id="civij59bv" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                The AI Assistant is available via the floating chat button in the bottom-right corner.
                You can click on it to start a conversation with our AI teaching assistant.
              </p>
              <Button
              onClick={onOpenFloatingChat}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white" data-id="tcpbp05f8" data-path="src/components/ai/EnhancedAIAssistant.tsx">

                <MessageCircle className="h-4 w-4 mr-2" data-id="foa70bplg" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
                Start New Conversation
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Capabilities */}
      <Card data-id="a8zsd1iam" data-path="src/components/ai/EnhancedAIAssistant.tsx">
        <CardHeader data-id="iub20zulp" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <CardTitle className="flex items-center gap-2" data-id="jbhu2uakc" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            <Sparkles className="h-5 w-5" data-id="6z8fa7q1g" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
            What can the AI Assistant help with?
          </CardTitle>
        </CardHeader>
        <CardContent data-id="mbaf0r32r" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <div className="grid gap-4 md:grid-cols-2" data-id="wgimje4qw" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            {/* Basic Features */}
            <div className="space-y-3" data-id="8kty49uae" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <h3 className="font-medium text-green-700 flex items-center gap-2" data-id="aa2husrf1" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                <Badge variant="secondary" className="bg-green-100 text-green-700" data-id="kaljwy4a0" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  Basic Features
                </Badge>
              </h3>
              <ul className="space-y-2 text-sm" data-id="brsldperx" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                <li className="flex items-start gap-2" data-id="ip71onmba" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  <div className="h-1.5 w-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" data-id="nv267mweq" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                  General teaching questions and advice
                </li>
                <li className="flex items-start gap-2" data-id="fe32rtcaj" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  <div className="h-1.5 w-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" data-id="hhxoc73dw" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                  Educational resource recommendations
                </li>
                <li className="flex items-start gap-2" data-id="2o1hl7vja" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  <div className="h-1.5 w-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" data-id="m5ldge2s0" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                  Classroom management tips
                </li>
                <li className="flex items-start gap-2" data-id="a18z7diza" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  <div className="h-1.5 w-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" data-id="8tha8xlb2" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                  Student engagement techniques
                </li>
              </ul>
            </div>

            {/* Advanced Features */}
            {(hasAIAccess('advanced') || isAdmin) &&
          <div className="space-y-3" data-id="ckt741giv" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                <h3 className="font-medium text-purple-700 flex items-center gap-2" data-id="j2i0up7qr" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700" data-id="z2yhj61r" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                    <Star className="h-3 w-3 mr-1" data-id="ojtnvg0ek" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
                    Advanced Features
                  </Badge>
                </h3>
                <ul className="space-y-2 text-sm" data-id="0q0comdvl" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                  <li className="flex items-start gap-2" data-id="y1quksgfg" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                    <div className="h-1.5 w-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" data-id="labu8n1j3" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                    Lesson planning and curriculum development
                  </li>
                  <li className="flex items-start gap-2" data-id="3jv0e8meo" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                    <div className="h-1.5 w-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" data-id="g3lpticlp" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                    Quiz and assessment creation
                  </li>
                  <li className="flex items-start gap-2" data-id="2ie5tr9pm" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                    <div className="h-1.5 w-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" data-id="3ayfkn100" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                    Advanced teaching strategies
                  </li>
                  <li className="flex items-start gap-2" data-id="znm1w0cqh" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                    <div className="h-1.5 w-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" data-id="ruy0o20w8" data-path="src/components/ai/EnhancedAIAssistant.tsx"></div>
                    Presentation and slide generation
                  </li>
                </ul>
              </div>
          }
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card data-id="qw4ffps08" data-path="src/components/ai/EnhancedAIAssistant.tsx">
        <CardHeader data-id="ja5pgir1z" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <CardTitle className="text-lg" data-id="hphx3vpea" data-path="src/components/ai/EnhancedAIAssistant.tsx">Tips for Better Conversations</CardTitle>
        </CardHeader>
        <CardContent data-id="670ejukwk" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <div className="grid gap-4 md:grid-cols-3" data-id="ms1o9tmcv" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            <div className="p-4 bg-blue-50 rounded-lg" data-id="v5mm6flzk" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <h4 className="font-medium text-blue-900 mb-2" data-id="6j2g2i9tb" data-path="src/components/ai/EnhancedAIAssistant.tsx">Be Specific</h4>
              <p className="text-sm text-blue-700" data-id="a4bka20vr" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                Include details about your grade level, subject, and specific challenges for more tailored advice.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg" data-id="7nn7zr40n" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <h4 className="font-medium text-green-900 mb-2" data-id="ruh1vyc1l" data-path="src/components/ai/EnhancedAIAssistant.tsx">Ask Follow-ups</h4>
              <p className="text-sm text-green-700" data-id="eswzaqma1" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                Don't hesitate to ask for clarification, examples, or additional resources on any topic.
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg" data-id="sm6kbpcbb" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <h4 className="font-medium text-purple-900 mb-2" data-id="4gbaa6lys" data-path="src/components/ai/EnhancedAIAssistant.tsx">Save Important Chats</h4>
              <p className="text-sm text-purple-700" data-id="u0oqvf3z6" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                Your conversation history is automatically saved and can be accessed from the History tab.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>;


  if (!(hasAIAccess('basic') || isAdmin)) {
    return (
      <div className="p-6" data-id="wrshd1hxs" data-path="src/components/ai/EnhancedAIAssistant.tsx">
        <Card data-id="5qwl1zmgc" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <CardContent className="p-6" data-id="eyelc403z" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="62b16qphn" data-path="src/components/ai/EnhancedAIAssistant.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="11392jowb" data-path="src/components/ai/EnhancedAIAssistant.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="whj3p0nb1" data-path="src/components/ai/EnhancedAIAssistant.tsx">
                AI Assistant requires at least Tier 1 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>);

  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-id="04kt9mq3j" data-path="src/components/ai/EnhancedAIAssistant.tsx">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6" data-id="gnh3zeygd" data-path="src/components/ai/EnhancedAIAssistant.tsx">

        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="zdiqunmfp" data-path="src/components/ai/EnhancedAIAssistant.tsx">AI Assistant</h1>
        <p className="text-gray-600" data-id="3e5e2einl" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          Access your AI teaching assistant and view your conversation history.
        </p>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-id="2iztedbgu" data-path="src/components/ai/EnhancedAIAssistant.tsx">
        <TabsList className="grid w-full grid-cols-3" data-id="q151zdsbt" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <TabsTrigger value="info" className="flex items-center gap-2" data-id="vdlbumuu5" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            <Info className="h-4 w-4" data-id="luyniuez0" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
            AI Information
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-id="lj7mq06ph" data-path="src/components/ai/EnhancedAIAssistant.tsx">
            <History className="h-4 w-4" data-id="usw62k1w7" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
            Chat History
          </TabsTrigger>
          <TabsTrigger
            value="viewer"
            className="flex items-center gap-2"
            disabled={!selectedConversationId} data-id="80z23ifef" data-path="src/components/ai/EnhancedAIAssistant.tsx">

            <MessageCircle className="h-4 w-4" data-id="51f9cdljw" data-path="src/components/ai/EnhancedAIAssistant.tsx" />
            Conversation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-6" data-id="1xjthd400" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          {renderAIInfo()}
        </TabsContent>

        <TabsContent value="history" className="space-y-6" data-id="jcowpeeaa" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }} data-id="g79jyvd2n" data-path="src/components/ai/EnhancedAIAssistant.tsx">

            <ConversationHistory
              onSelectConversation={handleSelectConversation}
              selectedConversationId={selectedConversationId} data-id="j9kspnile" data-path="src/components/ai/EnhancedAIAssistant.tsx" />

          </motion.div>
        </TabsContent>

        <TabsContent value="viewer" className="space-y-6" data-id="fzzfn3xhq" data-path="src/components/ai/EnhancedAIAssistant.tsx">
          {selectedConversationId &&
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }} data-id="spo8e2d7l" data-path="src/components/ai/EnhancedAIAssistant.tsx">

              <ConversationViewer
              conversationId={selectedConversationId}
              onBack={handleBackToHistory}
              onContinueConversation={handleContinueConversation} data-id="31ytxsmgf" data-path="src/components/ai/EnhancedAIAssistant.tsx" />

            </motion.div>
          }
        </TabsContent>
      </Tabs>
    </div>);

};

export default EnhancedAIAssistant;